import { useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { formatDate, formatNumber } from "../../lib/format";
import { PanelLoading } from "../../components/Loading";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  carriersApi,
  gateApi,
  tripsApi,
  type CarrierView,
  type DriverView,
  type TripView,
  type VehicleView,
  type WeighingView,
} from "../../api/transport";
import { deliveriesApi, type Delivery } from "../../api/deliveries";
import { ordersApi, type Order } from "../../api/orders";
import type { ApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { useConfirm, usePrompt } from "../../components/ConfirmDialog";
import { Select } from "../../components/Select";
import { AllocationSection } from "./AllocationSection";
import Ic from "../../components/icons.jsx";
import {
  canCancelTrip,
  canEditTripItems,
  carrierNameOf,
  counterpartiesOf,
  isMixedLoad,
  loadTotal,
  creatableTripDirections,
  driverNameOf,
  isDeliveryEligibleForTrip,
  isOrderEligibleForTrip,
  plateOf,
  statusTagClass,
  weighingTagClass,
} from "./tripsBoard";

/**
 * Trip detail — the screen a trip number
 * on the board (`TripsPage`) links into. Three read sections (trip data,
 * items, weighings) plus two gated actions that mutate the trip itself:
 * add/remove item and cancel. Recording a weighing is the gate's job (Task
 * 11's screen) — this page only reads them, via the sanctioned `trips:view`
 * exception on `GET /gate/{id}/weighings`.
 *
 * Item changes use Write access for the trip's direction, shared with the board's
 * create gate in `creatableTripDirections`. Cancelling the entire trip requires
 * Full access for that direction.
 *
 * Permission is only half of it, though: each action
 * also has a status half, `canEditTripItems` / `canCancelTrip`, mirroring the
 * server's own `INVALID_TRANSITION` refusals. Direction alone would let a
 * DEPARTED or CANCELLED trip still show "Add item" and "Remove" buttons.
 */
export default function TripDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const prompt = usePrompt();
  const confirm = useConfirm();
  const { hasPermission } = useAuth();

  const [trip, setTrip] = useState<TripView | null>(null);
  const [weighings, setWeighings] = useState<WeighingView[] | null>(null);
  const [carriers, setCarriers] = useState<CarrierView[]>([]);
  const [vehicles, setVehicles] = useState<VehicleView[]>([]);
  const [drivers, setDrivers] = useState<DriverView[]>([]);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    if (!id) return;
    try {
      setTrip(await tripsApi.get(id));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the route id changes
  useEffect(() => { void load(); }, [id]);

  useEffect(() => {
    if (!id) return;
    void gateApi.weighings(id).then(setWeighings).catch(() => setWeighings([]));
  }, [id]);

  // Carrier/vehicle/driver names for the trip-data section — TripView only
  // carries ids. Same best-effort load as TripsPage: a caller without
  // carriers:manage still holds trips:view, which CarrierController accepts.
  useEffect(() => {
    void carriersApi.list().then(setCarriers).catch(() => undefined);
    void carriersApi.listVehicles().then(setVehicles).catch(() => undefined);
    void carriersApi.listDrivers().then(setDrivers).catch(() => undefined);
  }, []);

  if (!trip) {
    return <PanelLoading text={t("common.loading")} />;
  }

  // The permission half of the gate for every mutating action on this page —
  // reused, not re-derived (see the module docstring above). Each action then
  // adds its own status half: a DEPARTED or CANCELLED trip accepts neither an
  // item change nor a cancellation, whoever is asking.
  const canManageThisTrip = creatableTripDirections(hasPermission).includes(trip.direction);
  const canDeleteThisTrip = hasPermission(`section:transport_${trip.direction.toLowerCase()}:full`);
  const canCancel = canDeleteThisTrip && canCancelTrip(trip.status);
  const canEditItems = canManageThisTrip && canEditTripItems(trip.status);

  async function removeItem(itemId: string) {
    if (!trip || !canEditItems) return;
    // On a mixed load a stray click deletes ANOTHER customer's cargo, so this
    // asks — the same courtesy cancelling the whole trip already gets.
    const ok = await confirm({
      title: t("trips.item.removeTitle", "Remove item"),
      message: t("trips.item.removeMessage", "Remove this load from the trip?"),
      confirmLabel: t("action.remove", "Remove"),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await tripsApi.removeItem(trip.id, itemId);
      await load();
      toast.success(t("trips.toast.itemRemoved", "Item removed."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function cancelTrip() {
    if (!trip || !canCancel) return;
    const reason = await prompt({
      title: t("trips.action.cancelTrip", "Cancel trip"),
      message: t("trips.cancel.message", "Why is this trip being cancelled?"),
      placeholder: t("trips.cancel.placeholder", "Reason (optional)"),
      multiline: true,
      confirmLabel: t("trips.action.cancelTrip", "Cancel trip"),
      danger: true,
    });
    if (reason === null) return; // dismissed
    setBusy(true);
    try {
      const updated = await tripsApi.cancel(trip.id, reason.trim() || undefined);
      setTrip(updated);
      toast.success(t("trips.toast.cancelled", "Trip cancelled."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  const fmtDateTime = (v: string | null) => (v ? new Date(v).toLocaleString(i18n.resolvedLanguage) : "—");

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label={t("trips.detail.breadcrumb.aria", "Breadcrumb")}>
            <span style={crumbMuted}>{t("nav.group.transport", "Transport")}</span>
            <span><Link to="/transport/trips" style={crumbLink}>{t("trips.title", "Trips")}</Link></span>
            <span style={crumbCurrent}>{trip.tripNumber}</span>
          </nav>
          <h1>{trip.tripNumber}</h1>
          <div className="sub">
            {t(`trips.direction.${trip.direction.toLowerCase()}`)}
            {" · "}
            <span className={`tag ${statusTagClass(trip.status)}`}>{t(`trips.status.${trip.status.toLowerCase()}`)}</span>
          </div>
        </div>
        <div className="page-actions">
          {canCancel && (
            <button type="button" className="btn" style={dangerBtn} onClick={() => void cancelTrip()} disabled={busy}>
              <Ic.x /> {t("trips.action.cancelTrip", "Cancel trip")}
            </button>
          )}
          <button type="button" onClick={() => navigate(-1)} className="btn">
            {t("action.back", "Back")}
          </button>
        </div>
      </div>

      <section style={card}>
        <h2 style={cardTitle}>{t("trips.section.data", "Trip data")}</h2>
        <Grid>
          <ReadField label={t("trips.col.tripNumber", "Trip number")} value={trip.tripNumber} mono />
          <ReadField label={t("trips.col.direction", "Direction")} value={t(`trips.direction.${trip.direction.toLowerCase()}`)} />
          <ReadField label={t("trips.col.status", "Status")} value={t(`trips.status.${trip.status.toLowerCase()}`)} />
          <ReadField label={t("trips.col.carrier", "Carrier")} value={carrierNameOf(trip.carrierId, carriers) ?? "—"} />
          <ReadField label={t("trips.col.vehicle", "Vehicle")} value={plateOf(trip.vehicleId, vehicles) ?? "—"} mono />
          <ReadField label={t("trips.field.trailer", "Trailer")} value={plateOf(trip.trailerId, vehicles) ?? "—"} mono />
          <ReadField label={t("trips.field.driver", "Driver")} value={driverNameOf(trip.driverId, drivers) ?? "—"} />
          <ReadField label={t("trips.field.plannedAt", "Planned date/time")} value={fmtDateTime(trip.plannedAt)} />
          <ReadField label={t("trips.field.gateIn", "Gate in")} value={fmtDateTime(trip.gateInAt)} />
          <ReadField label={t("trips.field.gateOut", "Gate out")} value={fmtDateTime(trip.gateOutAt)} />
        </Grid>
        {trip.note && (
          <div style={{ marginTop: 4 }}>
            <ReadField label={t("trips.field.note", "Note")} value={trip.note} />
          </div>
        )}
      </section>

      <section style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <h2 style={cardTitle}>{t("trips.section.items", "Items")}</h2>
            {/* A mixed load is legal and always was; it was simply invisible. A
                single-customer trip shows no badge and reads exactly as before. */}
            {isMixedLoad(trip.items) && (
              <span className="tag">
                {t("trips.item.mixed", "Mixed load · {{count}} counterparties", {
                  count: counterpartiesOf(trip.items).length,
                })}
              </span>
            )}
          </div>
          {canEditItems && (
            <button type="button" className="btn sm" onClick={() => setAddItemOpen(true)}>
              <Ic.plus /> {t("trips.action.addItem", "Add item")}
            </button>
          )}
        </div>
        {trip.items.length > 0 && (() => {
          // Never print a total that looks complete when it is not: quantityKg is
          // nullable, so a bare sum would silently omit rows.
          const { kg, unstated } = loadTotal(trip.items);
          return (
            <p style={{ margin: "0 0 8px", fontSize: 13, color: "var(--ink-2)" }}>
              {t("trips.item.loadTotal", "Load: {{kg}} kg", { kg: formatNumber(kg) })}
              {unstated > 0 && (
                <span style={{ color: "var(--ink-3)" }}>
                  {" · "}
                  {t("trips.item.unstated", "{{count}} item(s) with no mass entered", {
                    count: unstated,
                  })}
                </span>
              )}
            </p>
          );
        })()}
        {trip.items.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: "var(--ink-3)" }}>{t("trips.item.empty", "No items yet.")}</p>
        ) : (
          <table className="list-card">
            <thead>
              <tr>
                <th style={th}>{t("trips.col.position", "#")}</th>
                <th style={th}>{t("trips.col.counterparty", "Counterparty")}</th>
                <th style={th}>{t("trips.col.document", "Document")}</th>
                <th style={th}>{t("trips.col.quantity", "Quantity (kg)")}</th>
                <th style={th}>{t("trips.col.note", "Note")}</th>
                {canEditItems && <th style={th}>{t("trips.col.actions", "Actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {trip.items.map((item) => (
                <tr key={item.id}>
                  <td style={td} data-label={t("trips.col.position", "#")}>{item.position}</td>
                  <td style={td} data-label={t("trips.col.counterparty", "Counterparty")}>
                    {item.inScope ? (
                      <>
                        <span>{item.counterpartyName ?? "—"}</span>
                        {item.city && (
                          <span style={{ color: "var(--ink-3)" }}> · {item.city}</span>
                        )}
                      </>
                    ) : (
                      // The row stays so the load total stays honest; only the
                      // identity is withheld.
                      <span style={{ color: "var(--ink-3)", fontStyle: "italic" }}>
                        {t("trips.item.outOfScope", "Outside your scope")}
                      </span>
                    )}
                  </td>
                  <td style={td} data-label={t("trips.col.document", "Document")}>
                    {item.deliveryId ? (
                      <Link to={`/procurement/deliveries/${item.deliveryId}`}>
                        {t("trips.item.viewDelivery", "View delivery")}
                      </Link>
                    ) : item.orderId ? (
                      <Link to={`/orders/${item.orderId}`}>{t("trips.item.viewOrder", "View order")}</Link>
                    ) : "—"}
                  </td>
                  <td style={td} data-label={t("trips.col.quantity", "Quantity (kg)")}>
                    {item.quantityKg != null ? item.quantityKg : "—"}
                  </td>
                  <td style={td} data-label={t("trips.col.note", "Note")}>{item.note ?? "—"}</td>
                  {canEditItems && (
                    <td style={td} className="card-actions" data-label={t("trips.col.actions", "Actions")}>
                      <button type="button" className="btn sm" onClick={() => void removeItem(item.id)} disabled={busy}>
                        <Ic.x /> {t("action.remove", "Remove")}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Only an inbound lorry carrying more than one supplier needs a split: with
          a single supplier the whole net is theirs and the delivery's own receipt
          already records it. Outbound is refused server-side — those quantities
          come from the orders and our scale merely checks them. */}
      {trip.direction === "INBOUND" && trip.items.length > 1 && (
        <AllocationSection tripId={trip.id} canManage={canEditItems} />
      )}

      <section style={card}>
        <h2 style={cardTitle}>{t("trips.section.weighings", "Weighings")}</h2>
        {weighings === null ? (
          <p style={{ margin: 0, fontSize: 13, color: "var(--ink-3)" }}>{t("common.loading")}</p>
        ) : weighings.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: "var(--ink-3)" }}>{t("trips.weighing.empty", "No weighings recorded yet.")}</p>
        ) : (
          <table className="list-card">
            <thead>
              <tr>
                <th style={th}>{t("trips.weighing.col.kind", "Kind")}</th>
                <th style={th}>{t("trips.weighing.col.weight", "Weight (kg)")}</th>
                <th style={th}>{t("trips.weighing.col.scale", "Scale")}</th>
                <th style={th}>{t("trips.weighing.col.ticket", "Ticket no.")}</th>
                <th style={th}>{t("trips.weighing.col.time", "Time")}</th>
                <th style={th}>{t("trips.weighing.col.reason", "Reason")}</th>
              </tr>
            </thead>
            <tbody>
              {weighings.map((w) => (
                <tr key={w.id}>
                  <td style={td} data-label={t("trips.weighing.col.kind", "Kind")}>
                    <span className={`tag ${weighingTagClass(w.kind)}`}>
                      {t(`trips.weighing.kind.${w.kind.toLowerCase()}`)}
                    </span>
                  </td>
                  <td style={td} data-label={t("trips.weighing.col.weight", "Weight (kg)")}>{w.weightKg}</td>
                  <td style={td} data-label={t("trips.weighing.col.scale", "Scale")}>{w.scale ?? "—"}</td>
                  <td style={{ ...td, fontFamily: "var(--mono)" }} data-label={t("trips.weighing.col.ticket", "Ticket no.")}>
                    {w.ticketNo ?? "—"}
                  </td>
                  <td style={td} data-label={t("trips.weighing.col.time", "Time")}>{fmtDateTime(w.measuredAt)}</td>
                  <td style={td} data-label={t("trips.weighing.col.reason", "Reason")}>
                    {w.kind === "CONTROL" ? (w.reason ?? "—") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
          {t("trips.weighing.note", "Recording a weighing happens at the gate.")}
        </p>
      </section>

      {addItemOpen && (
        <AddItemDialog
          trip={trip}
          onClose={() => setAddItemOpen(false)}
          onAdded={async (updated) => {
            setTrip(updated);
            setAddItemOpen(false);
            toast.success(t("trips.toast.itemAdded", "Item added."));
          }}
        />
      )}
    </div>
  );
}

/**
 * Add-item form. The document picker's source list depends on the trip's own
 * direction (INBOUND -> deliveries, OUTBOUND -> orders) — never both, since
 * `TripService#assertItemMatchesDirection` would reject the mismatch anyway.
 * Every role allowed to open this dialog (`trips:manage_inbound`/`_outbound`)
 * already holds the matching `deliveries:view`/`orders:view` (OTKUP, SALES,
 * ADMIN, SUPERADMIN — see PermissionService), so these list calls are not
 * best-effort here the way label lookups elsewhere in this module are.
 *
 * The list is further filtered to `isDeliveryEligibleForTrip`/
 * `isOrderEligibleForTrip` (`tripsBoard.ts`) so the picker never offers a
 * document the server would now refuse with `ITEM_STATUS_NOT_ELIGIBLE` — a
 * REJECTED delivery or a DRAFT order simply does not appear. That eligibility
 * rule lives once, in `tripsBoard.ts`, mirroring `TripService`'s own
 * `ELIGIBLE_DELIVERY_STATUSES`/`ELIGIBLE_ORDER_STATUSES` — the server stays
 * the authority; this only avoids offering a dead end.
 *
 * Deliveries are asked for by origin as well: a receipt imported from the ERP
 * keeps the net mass weighed there, and the server refuses it on a trip with
 * `DELIVERY_FROM_ERP`. The filter belongs to the query, not to the page that
 * comes back, because the newest receipts are mostly imported ones and a page
 * cut first would leave the picker empty.
 */
export function AddItemDialog({
  trip, onClose, onAdded,
}: {
  trip: TripView;
  onClose: () => void;
  onAdded: (updated: TripView) => Promise<void>;
}) {
  const { t } = useTranslation();
  const isInbound = trip.direction === "INBOUND";
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [documentId, setDocumentId] = useState("");
  const [quantityKg, setQuantityKg] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (isInbound) {
      // The inbound picker: newest first, one page, like the order picker below; only deliveries
      // opened in the portal.
      void deliveriesApi.list({ origin: "PORTAL" }, 0, 200)
        .then((r) => setDeliveries(r.content))
        .catch(() => setDeliveries([]));
    } else {
      // Biranje porudžbine za turu: odobrene i otpremljene, najnovije prvo.
      void ordersApi.list({ size: 200 })
        .then((r) => setOrders(r.content))
        .catch(() => setOrders([]));
    }
  }, [isInbound]);

  const options = isInbound
    ? deliveries
        .filter((d) => isDeliveryEligibleForTrip(d.status))
        .map((d) => ({ value: d.id, label: `${d.companyName} — ${formatDate(d.announcedDate)}` }))
    : orders
        .filter((o) => isOrderEligibleForTrip(o.status))
        .map((o) => ({ value: o.id, label: `${o.orderNumber} — ${o.companyName}` }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!documentId) return;
    setBusy(true);
    setErr(null);
    try {
      const qty = quantityKg.trim() === "" ? undefined : Number(quantityKg);
      const updated = await tripsApi.addItem(trip.id, {
        deliveryId: isInbound ? documentId : undefined,
        orderId: isInbound ? undefined : documentId,
        quantityKg: qty,
        note: note.trim() === "" ? undefined : note.trim(),
      });
      await onAdded(updated);
    } catch (e2) {
      setErr((e2 as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={overlay} role="dialog" aria-modal="true" onClick={onClose}>
      <div style={dialog} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, fontSize: 16, color: "var(--ink)" }}>{t("trips.action.addItem", "Add item")}</h3>
        {err && <div style={errBox}>{err}</div>}
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label={isInbound ? t("trips.addItem.delivery", "Delivery") : t("trips.addItem.order", "Order")}>
            {options.length === 0 ? (
              <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
                {t("trips.addItem.noOptions", "Nothing available to add.")}
              </p>
            ) : (
              <Select<string>
                value={documentId}
                onChange={setDocumentId}
                ariaLabel={isInbound ? t("trips.addItem.delivery", "Delivery") : t("trips.addItem.order", "Order")}
                style={{ width: "100%" }}
                placeholder={t("trips.addItem.selectPlaceholder", "— Select —")}
                options={options}
              />
            )}
          </Field>
          <Field label={t("trips.col.quantity", "Quantity (kg)")}>
            <input
              type="number"
              min="0"
              step="0.01"
              value={quantityKg}
              onChange={(e) => setQuantityKg(e.target.value)}
              className="input"
              style={{ width: "100%" }}
            />
          </Field>
          <Field label={t("trips.col.note", "Note")}>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("trips.item.notePlaceholder", "e.g. ramp 3")}
              className="input"
              style={{ width: "100%" }}
            />
          </Field>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Cancel")}</button>
            <button type="submit" className="btn primary" disabled={busy || !documentId}>
              {busy ? "…" : t("trips.action.addItem", "Add item")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginTop: 4 }}>{children}</div>;
}

function ReadField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={fieldLabel}>{label}</span>
      <span style={{ fontSize: 13, color: "var(--ink)", fontFamily: mono ? "var(--mono)" : undefined }}>{value}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span style={fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

const card: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-md)", padding: 18,
  display: "flex", flexDirection: "column", gap: 8,
};
const cardTitle: CSSProperties = { margin: 0, fontSize: 14, color: "var(--ink)" };
const fieldLabel: CSSProperties = {
  display: "block", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4,
};
const th: CSSProperties = { textAlign: "left", padding: "8px 10px", fontSize: 12, color: "var(--ink-3)" };
const td: CSSProperties = { padding: "8px 10px", fontSize: 13 };
const crumbMuted: CSSProperties = { color: "var(--ink-4)" };
const crumbLink: CSSProperties = { color: "var(--ink-3)", textDecoration: "none" };
const crumbCurrent: CSSProperties = { color: "var(--ink-2)", fontWeight: 600 };
const dangerBtn: CSSProperties = { borderColor: "var(--err)", color: "var(--err)", background: "var(--surface)" };
const overlay: CSSProperties = {
  position: "fixed", inset: 0, background: "var(--overlay)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16,
};
const dialog: CSSProperties = {
  width: "100%", maxWidth: 480, maxHeight: "85vh", overflowY: "auto",
  background: "var(--surface)", padding: 24, borderRadius: 12,
  boxShadow: "var(--shadow-2)", display: "flex", flexDirection: "column", gap: 14,
};
const errBox: CSSProperties = {
  padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13,
};
